import { useRef, useEffect, useCallback, useState, useMemo } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import { enGB, es, pt, ptBR, it, ar } from 'date-fns/locale';

var useInterval = function(callback, delay) {
    var callbackRef = useRef(callback);
    useEffect(function() {
        callbackRef.current = callback;
    }, [
        callback
    ]);
    useEffect(function() {
        if (typeof window === "undefined" || delay === null) {
            return;
        }
        var id = window.setInterval(function() {
            return callbackRef.current();
        }, delay);
        return function() {
            window.clearInterval(id);
        };
    }, [
        delay
    ]);
};

function useKeyPress(key, callback) {
    var type = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : "keyup";
    var callbackRef = useRef(callback);
    useEffect(function() {
        callbackRef.current = callback;
    }, [
        callback
    ]);
    useEffect(function() {
        if (typeof window === "undefined") {
            return;
        }
        var onKeypress = function(e) {
            if (e.key === key) {
                callbackRef.current();
            }
        };
        window.addEventListener(type, onKeypress);
        return function() {
            window.removeEventListener(type, onKeypress);
        };
    }, [
        key,
        type
    ]);
}

var useBroadcastChannel = function(param) {
    var name = param.name, onMessage = param.onMessage, onError = param.onError;
    var channelRef = useRef();
    var listenersRef = useRef({
        onMessage: onMessage,
        onError: onError
    });
    listenersRef.current = {
        onMessage: onMessage,
        onError: onError
    };
    useEffect(function() {
        var _channelRef_current, _channelRef_current1;
        channelRef.current = typeof window !== "undefined" && "BroadcastChannel" in window ? new BroadcastChannel("".concat(name)) : undefined;
        var onMessage = function(e) {
            var _listenersRef_current_onMessage, _listenersRef_current;
            return (_listenersRef_current_onMessage = (_listenersRef_current = listenersRef.current).onMessage) === null || _listenersRef_current_onMessage === void 0 ? void 0 : _listenersRef_current_onMessage.call(_listenersRef_current, e);
        };
        var onError = function(e) {
            var _listenersRef_current_onError, _listenersRef_current;
            return (_listenersRef_current_onError = (_listenersRef_current = listenersRef.current).onError) === null || _listenersRef_current_onError === void 0 ? void 0 : _listenersRef_current_onError.call(_listenersRef_current, e);
        };
        (_channelRef_current = channelRef.current) === null || _channelRef_current === void 0 ? void 0 : _channelRef_current.addEventListener("message", onMessage);
        (_channelRef_current1 = channelRef.current) === null || _channelRef_current1 === void 0 ? void 0 : _channelRef_current1.addEventListener("messageerror", onError);
        return function() {
            var _channelRef_current, _channelRef_current1, _channelRef_current2;
            (_channelRef_current = channelRef.current) === null || _channelRef_current === void 0 ? void 0 : _channelRef_current.removeEventListener("message", onMessage);
            (_channelRef_current1 = channelRef.current) === null || _channelRef_current1 === void 0 ? void 0 : _channelRef_current1.removeEventListener("messageerror", onError);
            (_channelRef_current2 = channelRef.current) === null || _channelRef_current2 === void 0 ? void 0 : _channelRef_current2.close();
        };
    }, [
        name
    ]);
    var postMessage = useCallback(function(message) {
        var _channelRef_current;
        return (_channelRef_current = channelRef.current) === null || _channelRef_current === void 0 ? void 0 : _channelRef_current.postMessage(message);
    }, [
        channelRef
    ]);
    return {
        postMessage: postMessage
    };
};

function _array_like_to_array$1(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes$1(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit$1(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest$1() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array$1(arr, i) {
    return _array_with_holes$1(arr) || _iterable_to_array_limit$1(arr, i) || _unsupported_iterable_to_array$1(arr, i) || _non_iterable_rest$1();
}
function _unsupported_iterable_to_array$1(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array$1(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array$1(o, minLen);
}
/**
 * Use the provided query to fetch the data initially and cache it in the component state
 *
 *  Example:
 *  ```
 *      // Get and cache some data about a user with uid.
 *      // Passing the uid as a dependency means that the data will reload for a new uid
 *      // If you make changes client-side and want to force a refresh, call the refresh
 *      // function
 *      const [ user, refresh ] = useLocalQuery(()=>fetchUserDetailsApi(uid), [uid]);
 *  ```
 *
 *  In many cases react-query is a better solution than using this hook, but this does
 *  prevent the caching of queries which have a short lifespan, such as page data for
 *  a page which will be viewed once and then never looked at again.
 *
 * @param fetch promise that needs to resolve
 * @param deps readonly dependency array, updating causes a refresh
 * @param def default return to return
 * @returns
 */ var useLocalQuery = function(fetch) {
    var deps = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [], def = arguments.length > 2 ? arguments[2] : void 0;
    var _useState = _sliced_to_array$1(useState(), 2), data = _useState[0], setData = _useState[1];
    var _useState1 = _sliced_to_array$1(useState(), 2), error = _useState1[0], setError = _useState1[1];
    var refresh = useCallback(function() {
        setData(undefined);
        setError(undefined);
        return fetch().then(setData).catch(setError);
    }, [
        fetch
    ]);
    /* eslint-disable react-hooks/exhaustive-deps */ useEffect(function() {
        refresh();
    }, deps);
    return [
        data === undefined ? def === null || def === void 0 ? void 0 : def.data : data,
        refresh,
        error
    ];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
var useDebounced = function(fn, delay) {
    var debouncedFn = useMemo(function() {
        return debounce(fn, delay);
    }, [
        fn,
        delay
    ]);
    useEffect(function() {
        return debouncedFn.cancel();
    }, [
        debouncedFn
    ]);
    return debouncedFn;
};

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_with_holes(arr) {
    if (Array.isArray(arr)) return arr;
}
function _iterable_to_array_limit(arr, i) {
    var _i = arr == null ? null : typeof Symbol !== "undefined" && arr[Symbol.iterator] || arr["@@iterator"];
    if (_i == null) return;
    var _arr = [];
    var _n = true;
    var _d = false;
    var _s, _e;
    try {
        for(_i = _i.call(arr); !(_n = (_s = _i.next()).done); _n = true){
            _arr.push(_s.value);
            if (i && _arr.length === i) break;
        }
    } catch (err) {
        _d = true;
        _e = err;
    } finally{
        try {
            if (!_n && _i["return"] != null) _i["return"]();
        } finally{
            if (_d) throw _e;
        }
    }
    return _arr;
}
function _non_iterable_rest() {
    throw new TypeError("Invalid attempt to destructure non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _sliced_to_array(arr, i) {
    return _array_with_holes(arr) || _iterable_to_array_limit(arr, i) || _unsupported_iterable_to_array(arr, i) || _non_iterable_rest();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
var extractValueAndUnit = function(fontSize) {
    var _exec;
    var _ref = _sliced_to_array((_exec = /^([0-9.]+)([^0-9]*)$/.exec(fontSize)) !== null && _exec !== void 0 ? _exec : [], 3), value = _ref[1], unit = _ref[2];
    return [
        +value,
        unit
    ];
};
var textFit = function() {
    var padding = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 0;
    var container = null;
    var inner = null;
    var resize = function() {
        if (container && inner) {
            var boxWidth = container.clientWidth;
            inner.style.fontSize = "";
            var textWidth = inner.clientWidth;
            var _extractValueAndUnit = _sliced_to_array(extractValueAndUnit(getComputedStyle(container).fontSize), 2), originalFontSize = _extractValueAndUnit[0], unit = _extractValueAndUnit[1];
            var availableWidth = boxWidth - 2 * padding;
            var adjustedFontSize = originalFontSize * availableWidth / textWidth;
            var fontSize = Math.min(adjustedFontSize, originalFontSize);
            inner.style.fontSize = "".concat(fontSize).concat(unit);
        }
    };
    var mutationObserver = new MutationObserver(resize);
    var resizeObserver = new ResizeObserver(resize);
    return {
        setContainer: function(element) {
            resizeObserver.disconnect();
            container = element;
            container && resizeObserver.observe(container);
            resize();
        },
        setInner: function(element) {
            mutationObserver.disconnect();
            inner = element;
            inner && mutationObserver.observe(inner, {
                characterData: true,
                subtree: true
            });
            resize();
        },
        destroy: function() {
            mutationObserver.disconnect();
            resizeObserver.disconnect();
        },
        setPadding: function(newPadding) {
            padding = newPadding;
            resize();
        }
    };
};
var useTextFit = function() {
    var padding = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : 0;
    var _useState = _sliced_to_array(useState(function() {
        return textFit(padding);
    }), 1), instance = _useState[0];
    useEffect(function() {
        return instance.destroy;
    }, [
        instance
    ]);
    useEffect(function() {
        return instance.setPadding(padding);
    }, [
        instance,
        padding
    ]);
    return instance;
};

var dateFnsLocales = {
    en: enGB,
    es: es,
    pt: pt,
    "pt-BR": ptBR,
    "es-419": es,
    it: it,
    ar: ar
};
var useDateLocale = function() {
    var _useTranslation = useTranslation(), t = _useTranslation.t, language = _useTranslation.i18n.language;
    var _dateFnsLocales_language;
    return {
        dateFns: (_dateFnsLocales_language = dateFnsLocales[language]) !== null && _dateFnsLocales_language !== void 0 ? _dateFnsLocales_language : dateFnsLocales["en"],
        atHour: function(hour) {
            return t("common.dates.atHour", "at {{hour}}", {
                hour: hour
            });
        },
        tomorrowAtHour: function(hour) {
            return t("common.dates.tomorrowAtHour", "tomorrow at {{hour}}", {
                hour: hour
            });
        },
        dateAtHour: function(date, hour) {
            return t("common.dates.dateAtHour", "{{date}} at {{hour}}", {
                date: date,
                hour: hour
            });
        }
    };
};

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var RECAPTCHA_SCRIPT_ID = "recaptcha-script";
var useRecaptcha = function(siteKey) {
    var RECAPTCHA_SCRIPT_URL = "https://www.google.com/recaptcha/enterprise.js?render=".concat(siteKey);
    var loadScript = useCallback(function() {
        if (!document.getElementById(RECAPTCHA_SCRIPT_ID) && !!siteKey) {
            var script = document.createElement("script");
            script.id = RECAPTCHA_SCRIPT_ID;
            script.src = RECAPTCHA_SCRIPT_URL;
            window.document.head.append(script);
        }
    }, [
        RECAPTCHA_SCRIPT_URL,
        siteKey
    ]);
    useEffect(function() {
        loadScript();
    }, [
        loadScript
    ]);
    var getRecaptchaData = function() {
        var _ref = _async_to_generator(function(action) {
            return _ts_generator(this, function(_state) {
                if (typeof grecaptcha === "undefined") {
                    throw new Error("Attempted to use grecaptcha before script was loaded");
                }
                return [
                    2,
                    new Promise(function(resolve, reject) {
                        grecaptcha.enterprise.ready(function() {
                            grecaptcha.enterprise.execute(siteKey, {
                                action: action
                            }).then(function(token) {
                                var recaptchaData = {
                                    token: token,
                                    siteKey: siteKey,
                                    action: action
                                };
                                resolve(recaptchaData);
                            }).catch(function(error) {
                                reject(error);
                            });
                        });
                    })
                ];
            });
        });
        return function getRecaptchaData(action) {
            return _ref.apply(this, arguments);
        };
    }();
    return {
        getRecaptchaData: getRecaptchaData
    };
};

var useResizeObserver = function(elementRef, callback) {
    useEffect(function() {
        var element = elementRef.current;
        if (element) {
            var resizeObserver = new ResizeObserver(callback);
            resizeObserver.observe(element);
            return function() {
                resizeObserver.unobserve(element);
            };
        }
        return undefined;
    }, [
        elementRef,
        callback
    ]);
};

export { useBroadcastChannel, useDateLocale, useDebounced, useInterval, useKeyPress, useLocalQuery, useRecaptcha, useResizeObserver, useTextFit };
